import { getBootSnapshot, recoverBoot } from '#~/routes/root/boot-budget.js';
import { fetchAgentsForRoomStrict, getStatus } from '#~/api/agent/index.js';
// Spec 4 (D1): attribution for the agent-readiness lane.
import { beginRendererOperation, markRendererMilestone } from '#~/boot/renderer-startup.js';
import { wrapApiCall } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { useDepot, useProducer } from '#~/depot/hooks.js';
import { useIdentityState, useIdentityStore } from '#~/electron/identity-store.js';
import { StudioError } from '#~/error/index.js';
import { nats } from '#~/nats/index.js';
import { queryKeys } from '#~/react-query/index.js';
import { useAgents } from '../hooks.js';
import { shallow } from 'zustand/shallow';
import type { Agent, Status } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { useQuery } from '@tanstack/react-query';
import {
  AgentDirectSetStatusRequest,
  AgentDirectSetStatusResponse,
  AgentRunningState
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import { useRef } from 'react';

/** @internal */
export const sendSignal = async (agentId: Agent['id']): Promise<void> => {
  try {
    const reply = await wrapApiCall(async () => {
      const response = await nats.agentConnection(agentId).request(
        `direct.authenticated.${agentId}.signal`,
        AgentDirectSetStatusRequest.encode({
          $type: 'api.v1.AgentDirectSetStatusRequest',
          runningState: AgentRunningState.AGENT_RUNNING_STATE_START
        }).finish()
      );

      return AgentDirectSetStatusResponse.decode(response.data);
    });

    if (reply.runningState !== AgentRunningState.AGENT_RUNNING_STATE_START) {
      throw new Error('ST-81P');
    }
  } catch (cause) {
    throw new StudioError('Unable to send start signal to agent.', { cause });
  }
};

// Re-enumeration cadence while the boot agent read has NOT settled (see the
// queryFn below). 2s is fast enough to beat the user's patience on the boot
// overlay (the observed CI failure window — the cloud API becoming reachable
// again after a rapid refresh — is ~4-8s), and slow enough not to hammer a
// genuinely-down backend; a boot that never settles lands in the existing
// boot-stall recovery (boot-stall.ts, BOOT_STALL_DEADLINE_MS=45s).
// Per rule: e2e-tests-fail-fast-pass-fast analog for product waits — cadence,
// not a blind sleep. @internal exported for tests.
export const AGENT_ENUMERATION_RETRY_MS = 2_000;

/**
 * Refetch-interval policy for the boot agent enumeration: keep re-reading ONLY
 * while the last run did not settle (`false`); stop the moment it settles
 * (`true`) either as a real agent list or a genuine zero-agent room.
 * Pure + exported so the policy is unit-testable without driving timers.
 * @internal
 */
export const agentEnumerationRefetchInterval = (lastResult: boolean | undefined): number | false =>
  lastResult === false ? AGENT_ENUMERATION_RETRY_MS : false;

// How many consecutive settled-EMPTY enumerations to tolerate (as "not
// settled", re-reading on AGENT_ENUMERATION_RETRY_MS cadence) while THIS
// machine's identity says a Bridge is linked. A SUCCESSFUL GetRoom that
// returns zero agents for a linked machine is usually a transient server-side
// blank of the agent↔room association (CI rapid-refresh family,
// studio-refresh.spec.ts B-REFRESH-6: registry 403 storms blank RoomId, the
// read then "succeeds" with zero agents, and the readiness machine dismissed
// the BootOverlay onto the Linker for a Studio that HAS a linked Bridge).
// The strict-enumeration fix above only covers FAILED reads — this budget
// covers the succeeded-but-blank read. Bounded so a machine linked to a
// DIFFERENT studio on a genuinely empty studio still settles (~10s) instead
// of hanging (the 2026-08-07 create-new-studio QA hang must stay fixed).
// Per rule: absent-data-gates-permissively — the budget delays, never blocks.
export const LINKED_EMPTY_ENUMERATION_BUDGET = 5;

export const useRoomAgentStatus = (roomId: Room['id']) => {
  const producer = useProducer();
  const accountId = useDepot((µ) => µ.user?.id ?? null);
  const { getAccessTokenSilently } = useAuth0();
  const currentAgents = useAgents((a) => a.map((v) => v.id), shallow);
  const identity = useIdentityState();
  // Early sidecar `unlinked` is NOT final on Iris (identity-store: a non-prime
  // unlinked stays settled=false until the session ceiling / auto-link). Rapid
  // refresh fail-opens onto the Linker if we key only on status. Hive still
  // settles unlinked immediately (store Gate #3).
  // Per rule: absent-data-gates-permissively (ai.knowledge/global) — unknown
  // identity must not dismiss BootOverlay onto Linker.
  // See knowledge: hive-no-auto-link-bootstrap-contract (ai.knowledge/studio.client).
  const identitySettled = useIdentityStore((s) => s.settled);

  // 'linked' / 'resolving' empty reads use the budget. Unsettled unlinked is
  // held by the early return below — do not also list it here (unreachable).
  // Settled 'unlinked'/'error' fail OPEN so a genuine bridge-less boot is not delayed.
  const machineMaybeLinked = identity.status === 'linked' || identity.status === 'resolving';

  // Per-room budget of tolerated settled-empty reads (see constant above).
  // A roomId change is a different studio — fresh budget.
  const emptyReadsRef = useRef({ roomId, count: 0 });
  if (emptyReadsRef.current.roomId !== roomId) {
    emptyReadsRef.current = { roomId, count: 0 };
  }

  // Enumeration entries lack `status` (it is what this hook resolves); typing
  // the param as the enumeration shape avoids the previous `as Record<...,
  // Agent>` lie on the fetch result.
  const updateStatus = async (agent: Omit<Agent, 'status'>): Promise<Status> => {
    const agentId = agent.id;

    // Fetch the agent's status. Single-shot: getStatus no longer retries
    // internally. Retry cadence is owned by React-Query's refetch cycle below.
    const [status, inRoom] = await getStatus(agent);

    // Add the agent to the depot.
    producer((draft) => {
      if (inRoom && draft.agents[agentId]?.status === 'online') {
        return;
      }

      draft.agents[agentId] = { ...agent, status };
    });

    return status;
  };

  const { isLoading, data, error } = useQuery({
    queryKey: queryKeys.agentStatus(roomId, accountId),
    queryFn: async ({ client, queryKey }) => {
      // Spec 4 (D1): strict enumeration is one of the review's named 8s-cluster
      // candidates (five-attempt transport backoff + 2s refetch + empty-read
      // budget). Measure the token and the read separately, and record which
      // ANSWER the read produced — a "failed" and an "empty" enumeration cost the
      // same wall clock but mean completely different things.
      const closeToken = beginRendererOperation('agent.enumeration-token', 'agent');
      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently();
        closeToken('ok');
      } catch (authErr) {
        closeToken('error');
        // Auth failure during agent status fetch — signal boot error
        producer((draft) => {
          draft.boot.error = {
            message: 'Session expired. Please sign in again.',
            detail: authErr instanceof Error ? authErr.message : String(authErr),
            severity: 'fatal',
            retryFn: () => {
              producer((d) => {
                d.boot.error = null;
              });
              if (getBootSnapshot()) void recoverBoot('reload');
              else window.location.reload();
            }
          };
        });
        throw authErr;
      }

      // STRICT enumeration — a failed GetRoom read must stay distinguishable
      // from a room that genuinely has zero agents. The previous lenient call
      // (`fetchAgentsForRoom(...).catch(() => ({}))`) flattened every failure
      // to an empty agent set, which (a) opened the <AgentProvider> gate with
      // an empty depot and (b) let the room readiness machine conclude
      // "bridge-less" once EMPTY_STUDIO_GRACE_MS elapsed — dismissing the
      // BootOverlay onto the <Linker> ("First things first") for a Studio that
      // HAS a linked Bridge, with NO in-context recovery: this queryFn is the
      // only code path that ADDS agents to the depot, and with retry:false +
      // no refetch trigger the false-empty was final. (CI rapid-refresh family,
      // studio-refresh.spec.ts:161; the transport-retry-only fix #1077 was
      // falsified at its exact SHA — the failing error class is not
      // transient-classified, so retry-at-transport cannot close this.)
      // Returning `false` = "not settled": isReady stays false, the gate stays
      // closed behind the BootOverlay, and refetchInterval below re-enumerates
      // until the read settles either way. A settled-empty `{}` still falls
      // through to `return true` — the genuine bridge-less path is unchanged.
      // Per rule: reference-rules-in-comments + think-in-systems (ai.knowledge/global).
      let agents: Record<Agent['id'], Omit<Agent, 'status'>>;
      const closeEnumeration = beginRendererOperation('agent.strict-enumeration', 'agent');
      try {
        agents = await fetchAgentsForRoomStrict(roomId, accessToken);
        closeEnumeration('ok');
        markRendererMilestone(
          Object.keys(agents).length === 0
            ? 'agent.enumeration-empty'
            : 'agent.enumeration-nonempty'
        );
      } catch {
        closeEnumeration('error');
        // A failed RE-enumeration of an already-settled studio keeps the last
        // settled answer. In LOCAL mode with the LAN
        // healthy, a WAN drop followed >10s later by any focus/visibility
        // change (refetchOnWindowFocus) re-ran this read, GetRoom timed out,
        // the `false` below flipped isReady true→false, <AgentProvider>
        // rendered <Loader/> and BootOverlay took the studio over ("Loading
        // your studio…") for the whole outage — the one thing local mode must
        // never do. A failed READ is not evidence the room changed, so the
        // depot is left untouched too (no offline flip, no delete pass); the
        // next legitimate refetch re-checks. Keyed on this query's own
        // previous data rather than depot contents so a room switch (new
        // queryKey, stale depot) still boots behind the gate.
        // The boot-time contract is unchanged: never settled → `false` below.
        // Per rule: reference-rules-in-comments + think-in-systems (ai.knowledge/global).
        if (client.getQueryData<boolean>(queryKey) === true) {
          markRendererMilestone('agent.enumeration-failed-kept-settled');
          return true;
        }
        markRendererMilestone('agent.enumeration-failed');
        return false;
      }

      const updatedAgents = Object.values(agents);

      // Succeeded-but-EMPTY read on a machine whose Bridge is (possibly)
      // linked: within the bounded budget, report "not settled" so the
      // existing refetchInterval keeps re-enumerating — a transient
      // server-side blank recovers behind the BootOverlay instead of
      // dismissing it onto the Linker. Skips the deleted-agent cleanup below
      // on purpose: a suspicious blank must not wipe live depot agents.
      // See LINKED_EMPTY_ENUMERATION_BUDGET above for the full story.
      //
      // Identity not yet a final answer: do not consume the budget. Budget is
      // ~10s; iris patience is 30s. Exhausting here would still flash Linker
      // on Windows push-drop. Error is a hard terminal (always settled).
      if (updatedAgents.length === 0 && !identitySettled && identity.status !== 'error') {
        return false;
      }
      if (updatedAgents.length === 0 && machineMaybeLinked) {
        if (emptyReadsRef.current.count < LINKED_EMPTY_ENUMERATION_BUDGET) {
          emptyReadsRef.current.count += 1;

          return false;
        }
      } else if (updatedAgents.length > 0) {
        // Agents are back — restore the full budget for the next transient.
        emptyReadsRef.current.count = 0;
      }

      // Per rule: stay-in-scope (ai.knowledge/global) — one retry source of truth.
      // Per rule: think-in-systems (ai.knowledge/global) — remove layers, don't add them.
      // Previously, an offline result here triggered a fire-and-forget
      // `updateStatus(agent, 10, 1000)` — 10 retries at 1s intervals, stacking
      // across React-Query re-runs. That loop produced PostHog issue
      // `019cb0ab-2f62` (29,783 occurrences of "mutation request timed out" in
      // 12 days, 1,064/user average). The fix is to remove the fire-and-forget
      // entirely and let React-Query's normal refetch cycle (refetchOnWindowFocus
      // + staleTime below) handle re-checking. An offline agent stays offline in
      // the UI until the next legitimate refetch trigger. See
      // ~/iris/notes/spec-bridge-ux-reliability-fixes.md
      const promises = updatedAgents.map(async (agent) => {
        await updateStatus(agent);
      });

      // Wait for in-flight status updates to complete BEFORE running the
      // deleted-agent cleanup. If we ran the delete pass first, a late-resolving
      // updateStatus could re-add the just-deleted agent to the depot via its
      // producer call on line 54-60, producing zombie offline agents.
      // Pre-existing race; closed here because the retry-storm fix was already
      // editing this block (per `stay-in-scope`, in-scope as adjacent cleanup).
      await Promise.all(promises);

      const deletedAgents = currentAgents.filter((a) => !updatedAgents.find((u) => u.id === a));

      producer((draft) => {
        for (const agent of deletedAgents) {
          delete draft.agents[agent];
        }
      });

      return true;
    },
    // Suppress focus/blur-driven re-run storms. Any legitimate re-check after
    // less than 10s is served from cache. Bounds worst-case traffic from
    // rapid focus cycles (previously uncapped) to 1 query per 10s window.
    staleTime: 10_000,
    // Per rule: stay-in-scope + think-in-systems — one retry source of truth.
    // React-Query defaults to `retry: 3` with exponential backoff for any
    // queryFn throw. getStatus swallows exceptions so it never throws, but
    // getAccessTokenSilently() and (edge cases in) the enumeration can
    // throw, silently reintroducing a hidden retry layer. The whole point
    // of this fix is to eliminate hidden retry layers — so disable it here
    // explicitly. If the user needs a retry, a legitimate refetch trigger
    // (focus, invalidation, user action) handles it.
    retry: false,
    // Bounded re-enumeration ONLY while the read has not settled (data===false
    // from the strict-enumeration catch above). This is the single recovery
    // channel for a boot-time read failure — without it the boot would wait on
    // a refetch trigger that never comes on an already-focused window. Stops
    // the moment the read settles (true), so the happy path gains no traffic.
    refetchInterval: (query) => agentEnumerationRefetchInterval(query.state.data)
  });

  return { isLoading, isReady: data ?? false, error };
};
